import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Toolbar,
  Container,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import {
  createProfit,
  getProfitsForUser,
  getDepositsForUser, // ensure you add this helper to services/api
} from "../../services/api";

/**
 * AddProfit component (Admin)
 *
 * - Removed top-of-file eslint disable.
 * - fetchDeposits no longer directly reads selectedDepositId (uses functional setter).
 * - Hooks' dependency arrays are correct so eslint-plugin-react-hooks (if present) won't raise errors.
 */

export default function AddProfit() {
  const [rockstarId, setRockstarId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [selectedDepositId, setSelectedDepositId] = useState(null);

  const [fetching, setFetching] = useState(false);
  const [fetchingDeposits, setFetchingDeposits] = useState(false);
  const [recentProfits, setRecentProfits] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });
  const [errors, setErrors] = useState({});

  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // fetch profits
  const fetchRecent = useCallback(async (rId) => {
    if (!rId) {
      setRecentProfits([]);
      return;
    }
    setFetching(true);
    setErrors({});
    try {
      const res = await getProfitsForUser(rId);
      const data = res?.data?.data ?? res?.data ?? [];
      setRecentProfits(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchRecent error", err);
      setRecentProfits([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: err?.response?.data?.message || err?.message || "Failed to fetch profits",
      });
    } finally {
      setFetching(false);
    }
  }, []);

  // fetch deposits for this rockstarId
  // NOTE: does NOT read selectedDepositId directly to keep deps stable
  const fetchDeposits = useCallback(async (rId) => {
    if (!rId) {
      setDeposits([]);
      return;
    }
    setFetchingDeposits(true);
    try {
      const res = await getDepositsForUser(rId);
      const data = res?.data?.data ?? res?.data ?? [];
      const normalized = (Array.isArray(data) ? data : []).map((d) => ({
        ...d,
        amount: typeof d.amount === "string" ? Number(d.amount) : d.amount,
      }));
      setDeposits(normalized);

      // set default selected deposit only if nothing selected yet
      setSelectedDepositId((prev) => {
        if (prev) return prev;
        if (!normalized.length) return null;
        const pending = normalized.find((x) => (x.status || "").toLowerCase() === "pending");
        return (pending && pending.id) || normalized[0].id;
      });
    } catch (err) {
      console.error("fetchDeposits error", err);
      setDeposits([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: err?.response?.data?.message || err?.message || "Failed to fetch deposits",
      });
    } finally {
      setFetchingDeposits(false);
    }
  }, []);

  // when rockstar id changes -> fetch both
  useEffect(() => {
    if (!rockstarId) {
      setRecentProfits([]);
      setDeposits([]);
      setSelectedDepositId(null);
      return;
    }
    fetchRecent(rockstarId);
    fetchDeposits(rockstarId);
  }, [rockstarId, fetchRecent, fetchDeposits]);

  const validate = () => {
    const e = {};
    if (!rockstarId || !/^RBT\d+$/i.test(rockstarId)) e.rockstarId = "Enter a valid Rockstar ID (e.g. RBT1101)";
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) e.amount = "Enter a valid amount greater than 0";
    if (!selectedDepositId) e.deposit = "Select a deposit to attach this profit to";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async (ev) => {
    ev && ev.preventDefault();
    if (!validate()) {
      setSnackbar({ open: true, severity: "error", message: "Please fix validation errors" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { amount: Number(amount), note: note || "", depositId: selectedDepositId };
      await createProfit(rockstarId, payload);
      setSnackbar({ open: true, severity: "success", message: "Profit added successfully" });
      setAmount("");
      setNote("");
      // refresh lists
      await fetchRecent(rockstarId);
      await fetchDeposits(rockstarId);
    } catch (err) {
      console.error("handleCreate error", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err?.response?.data?.message || err?.message || "Failed to add profit",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // helper: find deposit by id
  const findDeposit = (id) => deposits.find((d) => Number(d.id) === Number(id));

  // compute profit percentage for display
  const profitPercent = (profit) => {
    if (!profit) return "—";
    const dep = findDeposit(profit.depositId);
    const pAmount = typeof profit.amount === "string" ? Number(profit.amount) : profit.amount;
    const dAmount = dep ? (typeof dep.amount === "string" ? Number(dep.amount) : dep.amount) : null;
    if (!dAmount || !Number.isFinite(dAmount) || dAmount === 0) return "—";
    const pct = (Number(pAmount) / Number(dAmount)) * 100;
    return `${pct.toFixed(2)}%`;
  };

  return (
    <Box>
      <Toolbar />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Add Profit (Attach to Deposit)
        </Typography>

        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2 }}>
          <form onSubmit={handleCreate}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  label="Rockstar ID"
                  value={rockstarId}
                  onChange={(e) => {
                    setRockstarId(e.target.value.trim().toUpperCase());
                    // reset selected deposit when id changes
                    setSelectedDepositId(null);
                  }}
                  fullWidth
                  placeholder="RBT1101"
                  error={Boolean(errors.rockstarId)}
                  helperText={errors.rockstarId}
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={Boolean(errors.deposit)}>
                  <InputLabel id="deposit-select-label">Select Deposit</InputLabel>
                  <Select
                    labelId="deposit-select-label"
                    value={selectedDepositId ?? ""}
                    label="Select Deposit"
                    onChange={(e) => setSelectedDepositId(e.target.value)}
                    disabled={fetchingDeposits || deposits.length === 0}
                  >
                    {deposits.length === 0 ? (
                      <MenuItem value="" disabled>
                        {fetchingDeposits ? "Loading deposits..." : "No deposits found"}
                      </MenuItem>
                    ) : (
                      deposits.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {`[${d.id}] ${d.amount} — ${d.createdAt ? new Date(d.createdAt).toLocaleString() : "—"}`}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.deposit && (
                    <Typography variant="caption" color="error">
                      {errors.deposit}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  label="Profit Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  fullWidth
                  placeholder="100.00"
                  error={Boolean(errors.amount)}
                  helperText={errors.amount}
                  inputMode="decimal"
                  required
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={12} sx={{ textAlign: "right" }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={16} /> : <MonetizationOnIcon />}
                  disabled={submitting}
                  sx={{ textTransform: "none" }}
                >
                  {submitting ? "Adding..." : "Add Profit"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="h6">User Deposits for {rockstarId || "—"}</Typography>
          <Divider sx={{ my: 1 }} />

          {fetchingDeposits ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : deposits.length === 0 ? (
            <Box sx={{ py: 2 }}>
              <Typography color="text.secondary">No deposits found for this Rockstar ID.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Submitted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deposits.map((d, idx) => (
                    <TableRow key={d.id} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{d.amount}</TableCell>
                      <TableCell>{d.status || "—"}</TableCell>
                      <TableCell>{d.createdAt ? new Date(d.createdAt).toLocaleString() : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6">Recent Profits for {rockstarId || "—"}</Typography>
          <Divider sx={{ my: 1 }} />

          {fetching ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : recentProfits.length === 0 ? (
            <Box sx={{ py: 2 }}>
              <Typography color="text.secondary">No profits found for this user.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Profit Amount</TableCell>
                    <TableCell>Deposit ID</TableCell>
                    <TableCell>Deposit Amount</TableCell>
                    <TableCell>Profit %</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Added By</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentProfits.map((p, idx) => {
                    const deposit = findDeposit(p.depositId);
                    return (
                      <TableRow key={p.id ?? idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{p.amount}</TableCell>
                        <TableCell>{p.depositId ?? "—"}</TableCell>
                        <TableCell>{deposit ? deposit.amount : "—"}</TableCell>
                        <TableCell>{profitPercent(p)}</TableCell>
                        <TableCell>{p.note || "—"}</TableCell>
                        <TableCell>{p.admin?.name || p.admin?.email || "—"}</TableCell>
                        <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleString() : "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
